/**
 * Phase 7.5 — light/dark/system theme.
 *
 * Three concerns split apart so each can be tested independently:
 *
 *  - `resolveEffectiveTheme(stored, systemDark)`: pure rule mapping
 *    a stored preference + system color-scheme to the actual class
 *    we add to `<html>`. Lives in this file's top half so Vitest
 *    can lock it in without DOM access.
 *
 *  - `applyTheme(effective)`: DOM side effect — toggles the `dark`
 *    class on `documentElement`. Tailwind v4's `@custom-variant
 *    dark (&:where(.dark, .dark *))` (in index.css) keys off this.
 *
 *  - `initThemeSync()`: at-boot wiring — reads localStorage, applies
 *    the resolved theme, and subscribes to `prefers-color-scheme`
 *    changes so a user on `system` mode sees the OS preference flip
 *    live.
 *
 * The `useThemeStore` Zustand store at `@/stores/useThemeStore` is
 * the React-side handle; this file owns the headless logic.
 */

export type ThemePreference = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "theme";

/**
 * Pure mapping from stored preference + system pref → effective theme.
 *
 *  - "light" / "dark": passthrough (user explicitly chose).
 *  - "system" (or null/missing): mirror the OS via `systemDark`.
 *
 * `null` is treated identically to "system" so a fresh user with no
 * stored value still gets the OS-respecting default.
 */
export function resolveEffectiveTheme(
  stored: ThemePreference | null | undefined,
  systemDark: boolean,
): EffectiveTheme {
  if (stored === "light") return "light";
  if (stored === "dark") return "dark";
  return systemDark ? "dark" : "light";
}

/** True when the OS is currently in dark mode. */
export function getSystemPrefersDark(): boolean {
  // `matchMedia` is universally available in Vite-supported targets
  // but defensive null-check in case this gets called server-side
  // by a future SSR experiment.
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Read the persisted preference. Returns null when nothing is stored. */
export function readStoredTheme(): ThemePreference | null {
  if (typeof localStorage === "undefined") return null;
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return null;
}

/** Persist the preference. Pass `null` to clear (reset to system). */
export function writeStoredTheme(pref: ThemePreference | null): void {
  if (typeof localStorage === "undefined") return;
  if (pref === null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, pref);
}

/**
 * Toggle the `dark` class on `<html>`. Idempotent — no-op when the
 * class is already in the desired state.
 */
export function applyTheme(effective: EffectiveTheme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

/**
 * Boot-time wiring: apply the stored theme right away (ideally before
 * first paint) and subscribe to system-pref changes so a "system"
 * user sees OS flips live.
 *
 * Returns an unsubscribe function. Boot callers can ignore it; the
 * subscription lives for the page's lifetime.
 */
export function initThemeSync(): () => void {
  const stored = readStoredTheme();
  applyTheme(resolveEffectiveTheme(stored, getSystemPrefersDark()));

  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: dark)");

  // Re-resolve on every OS-pref change. Reads `readStoredTheme()`
  // each time so a user toggling between explicit + system in the
  // settings UI doesn't need to teardown/recreate the listener.
  function handler(e: MediaQueryListEvent) {
    const current = readStoredTheme();
    if (current === "light" || current === "dark") return; // Explicit pref wins.
    applyTheme(e.matches ? "dark" : "light");
  }

  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
